var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _instance, _observer, _current, _disabled, _interval, _scroll, _resizeObserver, _intersectionObserver, _visibleSlides, _Slideshow_instances, setupSlideshowWithoutControls_fn, setupSlideshow_fn, _handleScroll, _onTransitionInit, _onTransitionEnd, _sync, _dragging, _handleMouseDown, _handlePointerEnter, _handleVisibilityChange, updateControlsVisibility_fn, setupIntersectionObserver_fn, centerSelectedThumbnail_fn, updateVisibleSlides_fn;
import { Component } from "@theme/component";
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler
} from "@theme/utilities";
import { Scroller, scrollIntoView } from "@theme/scrolling";
import { SlideshowSelectEvent } from "@theme/events";
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const _SlideshowViewportObserver = class _SlideshowViewportObserver {
  constructor() {
    /** @type {IntersectionObserver | null} */
    __privateAdd(this, _observer, null);
  }
  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!__privateGet(this, _instance)) {
      __privateSet(this, _instance, new _SlideshowViewportObserver());
    }
    return __privateGet(this, _instance);
  }
  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!__privateGet(this, _observer)) {
      __privateSet(this, _observer, new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = (
              /** @type {Slideshow} */
              entry.target
            );
            if (entry.isIntersecting) {
              slideshowElement.setAttribute("in-viewport", "");
            } else {
              slideshowElement.removeAttribute("in-viewport");
            }
          }
        },
        {
          rootMargin: "100px"
        }
      ));
    }
    __privateGet(this, _observer).observe(slideshow);
  }
  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    var _a;
    (_a = __privateGet(this, _observer)) == null ? void 0 : _a.unobserve(slideshow);
    slideshow.removeAttribute("in-viewport");
  }
};
_instance = new WeakMap();
_observer = new WeakMap();
/** @type {SlideshowViewportObserver | null} */
__privateAdd(_SlideshowViewportObserver, _instance, null);
let SlideshowViewportObserver = _SlideshowViewportObserver;
const _Slideshow = class _Slideshow extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _Slideshow_instances);
    __publicField(this, "requiredRefs", ["scroller"]);
    /**
     * The current slide index.
     * @type {number}
     */
    __privateAdd(this, _current, 0);
    /**
     * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
     * @type {boolean}
     */
    __privateAdd(this, _disabled, false);
    /**
     * The interval ID for automatic playback.
     * @type {number|undefined}
     */
    __privateAdd(this, _interval);
    /**
     * The Scroller instance that manages scrolling.
     * @type {Scroller}
     */
    __privateAdd(this, _scroll);
    /**
     * The ResizeObserver instance for monitoring scroller size changes
     * @type {ResizeObserver}
     */
    __privateAdd(this, _resizeObserver);
    /**
     * IntersectionObserver for efficient visibility tracking of slides
     * @type {IntersectionObserver | null}
     */
    __privateAdd(this, _intersectionObserver, null);
    /**
     * Cached visible slides result from IntersectionObserver
     * @type {HTMLElement[]}
     */
    __privateAdd(this, _visibleSlides, []);
    /**
     * Callback invoked on user initiated scroll to sync the current slide index
     * and emit a slide change event if the index has changed.
     */
    __privateAdd(this, _handleScroll, () => {
      var _a;
      const previousIndex = __privateGet(this, _current);
      const index = __privateGet(this, _sync).call(this);
      if (index === previousIndex) return;
      const slide = (_a = this.slides) == null ? void 0 : _a[index];
      if (!slide) return;
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: true,
          trigger: "scroll",
          slide,
          id: slide.getAttribute("slide-id")
        })
      );
    });
    __privateAdd(this, _onTransitionInit, () => {
      this.setAttribute("transitioning", "");
    });
    __privateAdd(this, _onTransitionEnd, () => {
      __privateMethod(this, _Slideshow_instances, updateVisibleSlides_fn).call(this);
      this.removeAttribute("transitioning");
    });
    /**
     * Synchronizes the scroll position and updates the current slide index.
     * @returns {number} The index of the current slide.
     */
    __privateAdd(this, _sync, () => {
      const { slides } = this;
      if (!slides) return this.current = 0;
      if (!__privateGet(this, _scroll)) return this.current = 0;
      const visibleSlides = this.visibleSlides;
      if (!visibleSlides.length) return this.current;
      const { axis } = __privateGet(this, _scroll);
      const { scroller } = this.refs;
      const centers = visibleSlides.map((slide) => center(slide, axis));
      const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
      const closestCenter = closest(centers, referencePoint);
      const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];
      if (!closestVisibleSlide) return this.current = 0;
      const index = slides.indexOf(closestVisibleSlide);
      return this.current = index;
    });
    __privateAdd(this, _dragging, false);
    /**
     * Handles the 'mousedown' event to start dragging slides.
     * @param {MouseEvent} event - The mousedown event.
     */
    __privateAdd(this, _handleMouseDown, (event) => {
      const { slides } = this;
      if (!slides || slides.length <= 1) return;
      if (!(event.target instanceof Element)) return;
      if (this.disabled || __privateGet(this, _dragging)) return;
      if (event.target.closest("model-viewer")) {
        return;
      }
      event.preventDefault();
      const { axis } = __privateGet(this, _scroll);
      const startPosition = event[axis];
      const controller = new AbortController();
      const { signal } = controller;
      const startTime = performance.now();
      let previous = startPosition;
      let velocity = 0;
      let moved = false;
      let distanceTravelled = 0;
      __privateSet(this, _dragging, true);
      const onPointerMove = (event2) => {
        var _a;
        const current = event2[axis];
        const initialDelta = startPosition - current;
        if (!initialDelta) return;
        if (!moved) {
          moved = true;
          this.setPointerCapture(event2.pointerId);
          document.addEventListener("click", preventDefault, { once: true, signal });
          const movingRight = initialDelta < 0;
          const movingLeft = initialDelta > 0;
          const closestSlideshow = (_a = this.parentElement) == null ? void 0 : _a.closest("slideshow-component");
          const isNested = closestSlideshow instanceof _Slideshow && closestSlideshow !== this;
          const cannotMoveInDirection = movingRight && this.atStart || movingLeft && this.atEnd;
          if (isNested && cannotMoveInDirection) {
            controller.abort();
            return;
          }
          this.pause();
          this.setAttribute("dragging", "");
        }
        event2.stopImmediatePropagation();
        const delta = previous - current;
        const timeDelta = performance.now() - startTime;
        velocity = Math.round(delta / timeDelta * 1e3);
        previous = current;
        distanceTravelled += Math.abs(delta);
        __privateGet(this, _scroll).by(delta, { instant: true });
      };
      const onPointerUp = async (event2) => {
        controller.abort();
        const { current, slides: slides2 } = this;
        const { scroller } = this.refs;
        __privateSet(this, _dragging, false);
        if (!(slides2 == null ? void 0 : slides2.length) || !scroller) return;
        const direction = Math.sign(velocity);
        const next = __privateGet(this, _sync).call(this);
        const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
        const newIndex = clamp(next + modifier, 0, slides2.length - 1);
        const newSlide = slides2[newIndex];
        const currentIndex = this.current;
        if (!newSlide) throw new Error("Slide not found at index ".concat(newIndex));
        __privateGet(this, _scroll).to(newSlide);
        this.removeAttribute("dragging");
        this.releasePointerCapture(event2.pointerId);
        __privateMethod(this, _Slideshow_instances, centerSelectedThumbnail_fn).call(this, newIndex);
        this.dispatchEvent(
          new SlideshowSelectEvent({
            index: newIndex,
            previousIndex: currentIndex,
            userInitiated: true,
            trigger: "drag",
            slide: newSlide,
            id: newSlide.getAttribute("slide-id")
          })
        );
        this.current = newIndex;
        await __privateGet(this, _scroll).finished;
        if (__privateGet(this, _dragging)) return;
        __privateGet(this, _scroll).snap = true;
        this.resume();
      };
      __privateGet(this, _scroll).snap = false;
      document.addEventListener("pointermove", onPointerMove, { signal });
      document.addEventListener("pointerup", onPointerUp, { signal });
      document.addEventListener("pointerdown", onPointerUp, { signal });
      document.addEventListener("pointercancel", onPointerUp, { signal });
      document.addEventListener("pointercapturelost", onPointerUp, { signal });
    });
    __privateAdd(this, _handlePointerEnter, () => {
      this.setAttribute("actioned", "");
    });
    /**
     * Pause the slideshow when the page is hidden.
     */
    __privateAdd(this, _handleVisibilityChange, () => document.hidden ? this.suspend() : this.resume());
  }
  static get observedAttributes() {
    return ["initial-slide"];
  }
  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "initial-slide" && oldValue !== newValue) {
      queueMicrotask(() => {
        var _a;
        if (!this.isConnected || !__privateGet(this, _scroll) || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = (_a = this.refs.slides[index]) == null ? void 0 : _a.getAttribute("slide-id");
        if (slide_id) {
          this.select({ id: slide_id }, void 0, { animate: false });
        }
      });
    }
  }
  async connectedCallback() {
    var _a;
    super.connectedCallback();
    SlideshowViewportObserver.getInstance().observe(this);
    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }
    const slideCount = ((_a = this.slides) == null ? void 0 : _a.length) || 0;
    slideCount <= 1 ? __privateMethod(this, _Slideshow_instances, setupSlideshowWithoutControls_fn).call(this) : __privateMethod(this, _Slideshow_instances, setupSlideshow_fn).call(this);
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    SlideshowViewportObserver.getInstance().unobserve(this);
    if (__privateGet(this, _scroll)) {
      const { scroller } = this.refs;
      scroller.removeEventListener("mousedown", __privateGet(this, _handleMouseDown));
      __privateGet(this, _scroll).destroy();
    }
    const slideCount = ((_a = this.slides) == null ? void 0 : _a.length) || 0;
    if (slideCount > 1) {
      this.removeEventListener("mouseenter", this.suspend);
      this.removeEventListener("mouseleave", this.resume);
      this.removeEventListener("pointerenter", __privateGet(this, _handlePointerEnter));
      document.removeEventListener("visibilitychange", __privateGet(this, _handleVisibilityChange));
    }
    if (__privateGet(this, _resizeObserver)) {
      __privateGet(this, _resizeObserver).disconnect();
    }
    if (__privateGet(this, _intersectionObserver)) {
      __privateGet(this, _intersectionObserver).disconnect();
      __privateSet(this, _intersectionObserver, null);
    }
  }
  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    var _a;
    return ((_a = this.parentElement) == null ? void 0 : _a.closest("slideshow-component")) !== null;
  }
  get initialSlide() {
    var _a;
    return (_a = this.refs.slides) == null ? void 0 : _a[this.initialSlideIndex];
  }
  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    var _a, _b, _c;
    if (__privateGet(this, _disabled) || !((_a = this.refs.slides) == null ? void 0 : _a.length)) return;
    if (!__privateGet(this, _scroll)) return;
    const currentSlide = (_b = this.slides) == null ? void 0 : _b[this.current];
    for (const slide2 of this.refs.slides) {
      if (slide2.hasAttribute("reveal")) {
        slide2.removeAttribute("reveal");
        slide2.setAttribute("aria-hidden", "true");
      }
    }
    let requestedIndex = (() => {
      if (typeof input === "number") return input;
      if (typeof input === "string") return parseInt(input, 10);
      if ("id" in input) {
        const requestedSlide = this.refs.slides.find((slide2) => slide2.getAttribute("slide-id") == input.id);
        if (!requestedSlide || !this.slides) return;
        if (requestedSlide.hasAttribute("hidden")) {
          requestedSlide.setAttribute("reveal", "");
          requestedSlide.setAttribute("aria-hidden", "false");
        }
        return this.slides.indexOf(requestedSlide);
      }
    })();
    const { current } = this;
    const { slides } = this;
    if (!(slides == null ? void 0 : slides.length) || requestedIndex === void 0 || isNaN(requestedIndex)) return;
    const requestedSlideElement = slides == null ? void 0 : slides[requestedIndex];
    if (currentSlide === requestedSlideElement) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);
    event == null ? void 0 : event.preventDefault();
    const { animate = true } = options;
    const lastIndex = slides.length - 1;
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;
    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      __privateSet(this, _disabled, true);
      await __privateGet(this, _scroll).finished;
      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;
      const placeholder = document.createElement("slideshow-slide");
      targetSlide.before(placeholder);
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }
      if (current === 0) __privateGet(this, _scroll).to(currentSlide, { instant: true });
      queueMicrotask(async () => {
        await __privateGet(this, _scroll).finished;
        __privateSet(this, _disabled, false);
        placeholder.replaceWith(targetSlide);
        __privateGet(this, _scroll).to(targetSlide, { instant: true });
        requestAnimationFrame(() => {
          this.setAttribute("refreshing-timeline", "");
          requestAnimationFrame(() => {
            this.removeAttribute("refreshing-timeline");
          });
        });
      });
    }
    const slide = slides[index];
    if (!slide) return;
    const previousIndex = this.current;
    slide.setAttribute("aria-hidden", "false");
    if (__privateGet(this, _scroll)) {
      __privateGet(this, _scroll).to(slide, { instant });
    }
    this.current = ((_c = this.slides) == null ? void 0 : _c.indexOf(slide)) || 0;
    __privateMethod(this, _Slideshow_instances, centerSelectedThumbnail_fn).call(this, index, instant ? "instant" : "smooth");
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: "select",
        slide,
        id: slide.getAttribute("slide-id")
      })
    );
  }
  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event == null ? void 0 : event.preventDefault();
    this.select(this.nextIndex, event, options);
  }
  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event == null ? void 0 : event.preventDefault();
    this.select(this.previousIndex, event, options);
  }
  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (__privateGet(this, _interval)) return;
    this.paused = false;
    __privateSet(this, _interval, setInterval(() => {
      if (this.matches(":hover") || document.hidden) return;
      this.next();
    }, interval));
  }
  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }
  get paused() {
    return this.hasAttribute("paused");
  }
  set paused(value) {
    if (value) {
      this.setAttribute("paused", "");
    } else {
      this.removeAttribute("paused");
    }
  }
  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(__privateGet(this, _interval));
    __privateSet(this, _interval, void 0);
  }
  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }
  get autoplay() {
    return Boolean(this.autoplayInterval);
  }
  get autoplayInterval() {
    const interval = this.getAttribute("autoplay");
    const value = parseInt("".concat(interval), 10);
    if (Number.isNaN(value)) return void 0;
    return value * 1e3;
  }
  get current() {
    return __privateGet(this, _current);
  }
  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;
    __privateSet(this, _current, value);
    if (current) current.textContent = "".concat(value + 1);
    for (const controls of [thumbnails, dots]) {
      controls == null ? void 0 : controls.forEach((el, i) => el.setAttribute("aria-selected", "".concat(i === value)));
    }
    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }
  get infinite() {
    return this.getAttribute("infinite") != null;
  }
  get visibleSlides() {
    return __privateGet(this, _visibleSlides);
  }
  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
    return current - modifier;
  }
  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
    return current + modifier;
  }
  get atStart() {
    const { current, slides } = this;
    return (slides == null ? void 0 : slides.length) ? current === 0 : false;
  }
  get atEnd() {
    const { current, slides } = this;
    return (slides == null ? void 0 : slides.length) ? current === slides.length - 1 : false;
  }
  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute("disabled", String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return this.getAttribute("disabled") === "true" || this.hasAttribute("mobile-disabled") && !mediaQueryLarge.matches;
  }
  get slides() {
    var _a;
    return (_a = this.refs.slides) == null ? void 0 : _a.filter((slide) => !slide.hasAttribute("hidden") || slide.hasAttribute("reveal"));
  }
  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute("initial-slide");
    if (initialSlide == null) return 0;
    return parseInt(initialSlide, 10);
  }
};
_current = new WeakMap();
_disabled = new WeakMap();
_interval = new WeakMap();
_scroll = new WeakMap();
_resizeObserver = new WeakMap();
_intersectionObserver = new WeakMap();
_visibleSlides = new WeakMap();
_Slideshow_instances = new WeakSet();
/**
 * Setup the slideshow without controls for zero or one slides
 */
setupSlideshowWithoutControls_fn = function() {
  var _a;
  this.current = 0;
  if (this.hasAttribute("auto-hide-controls")) {
    const { slideshowControls } = this.refs;
    if (slideshowControls instanceof HTMLElement) {
      slideshowControls.hidden = true;
    }
  }
  if ((_a = this.refs.slides) == null ? void 0 : _a[0]) {
    this.refs.slides[0].setAttribute("aria-hidden", "false");
  }
};
/**
 * Setup the slideshow with controls for when there are multiple slides
 */
setupSlideshow_fn = function() {
  __privateMethod(this, _Slideshow_instances, setupIntersectionObserver_fn).call(this);
  const { scroller } = this.refs;
  __privateSet(this, _scroll, new Scroller(scroller, {
    onScroll: __privateGet(this, _handleScroll),
    onScrollStart: __privateGet(this, _onTransitionInit),
    onScrollEnd: __privateGet(this, _onTransitionEnd)
  }));
  scroller.addEventListener("mousedown", __privateGet(this, _handleMouseDown));
  this.addEventListener("mouseenter", this.suspend);
  this.addEventListener("mouseleave", this.resume);
  this.addEventListener("pointerenter", __privateGet(this, _handlePointerEnter));
  document.addEventListener("visibilitychange", __privateGet(this, _handleVisibilityChange));
  __privateMethod(this, _Slideshow_instances, updateControlsVisibility_fn).call(this);
  this.disabled = this.isNested || this.disabled;
  this.resume();
  this.current = this.initialSlideIndex;
  scheduler.schedule(() => {
    var _a;
    let visibleSlidesAmount = 0;
    const initialSlideId = (_a = this.initialSlide) == null ? void 0 : _a.getAttribute("slide-id");
    requestAnimationFrame(() => {
      if (this.initialSlideIndex !== 0 && initialSlideId) {
        this.select({ id: initialSlideId }, void 0, { animate: false });
        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount = __privateMethod(this, _Slideshow_instances, updateVisibleSlides_fn).call(this);
        if (visibleSlidesAmount === 0) {
          this.select(0, void 0, { animate: false });
          visibleSlidesAmount = 1;
        }
      }
    });
    __privateSet(this, _resizeObserver, new ResizeObserver(async () => {
      if (viewTransition.current) await viewTransition.current;
      if (visibleSlidesAmount > 1) {
        __privateMethod(this, _Slideshow_instances, updateVisibleSlides_fn).call(this);
      }
      if (this.hasAttribute("auto-hide-controls")) {
        __privateMethod(this, _Slideshow_instances, updateControlsVisibility_fn).call(this);
      }
    }));
    __privateGet(this, _resizeObserver).observe(this.refs.slideshowContainer);
  });
};
_handleScroll = new WeakMap();
_onTransitionInit = new WeakMap();
_onTransitionEnd = new WeakMap();
_sync = new WeakMap();
_dragging = new WeakMap();
_handleMouseDown = new WeakMap();
_handlePointerEnter = new WeakMap();
_handleVisibilityChange = new WeakMap();
updateControlsVisibility_fn = function() {
  if (!this.hasAttribute("auto-hide-controls")) return;
  const { scroller, slideshowControls } = this.refs;
  if (!(slideshowControls instanceof HTMLElement)) return;
  slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
};
/**
 * Setup IntersectionObserver for efficient visibility tracking of slides
 */
setupIntersectionObserver_fn = function() {
  const { slides, scroller } = this.refs;
  if (!(slides == null ? void 0 : slides.length)) return;
  if (__privateGet(this, _intersectionObserver)) {
    __privateGet(this, _intersectionObserver).disconnect();
  }
  __privateSet(this, _intersectionObserver, new IntersectionObserver(
    (entries) => {
      const allEntries = [
        ...entries,
        ...__privateGet(this, _intersectionObserver) ? __privateGet(this, _intersectionObserver).takeRecords() : []
      ];
      for (const entry of allEntries) {
        const slide = (
          /** @type {HTMLElement} */
          entry.target
        );
        const isCurrentlyVisible = __privateGet(this, _visibleSlides).includes(slide);
        const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;
        if (shouldBeVisible && !isCurrentlyVisible) {
          __privateGet(this, _visibleSlides).push(slide);
        } else if (!shouldBeVisible && isCurrentlyVisible) {
          const index = __privateGet(this, _visibleSlides).indexOf(slide);
          if (index > -1) {
            __privateGet(this, _visibleSlides).splice(index, 1);
          }
        }
      }
      __privateGet(this, _visibleSlides).sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
      __privateMethod(this, _Slideshow_instances, updateVisibleSlides_fn).call(this);
    },
    {
      root: scroller,
      threshold: SLIDE_VISIBLITY_THRESHOLD,
      // Add small margin to account for sub-pixel rendering
      rootMargin: "1px"
    }
  ));
  slides.forEach((slide) => {
    var _a;
    (_a = __privateGet(this, _intersectionObserver)) == null ? void 0 : _a.observe(slide);
  });
};
/**
 * Centers the selected thumbnail in the thumbnails container
 * @param {number} index - The index of the selected thumbnail
 * @param {ScrollBehavior} [behavior] - The scroll behavior.
 */
centerSelectedThumbnail_fn = function(index, behavior = "smooth") {
  var _a;
  const selectedThumbnail = (_a = this.refs.thumbnails) == null ? void 0 : _a[index];
  if (!selectedThumbnail) return;
  const { thumbnailsContainer } = this.refs;
  if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
  const { slideshowControls } = this.refs;
  if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;
  scrollIntoView(selectedThumbnail, {
    ancestor: thumbnailsContainer,
    behavior,
    block: "center",
    inline: "center"
  });
};
updateVisibleSlides_fn = function() {
  const { slides } = this;
  if (!slides || !slides.length) return 0;
  const visibleSlides = this.visibleSlides;
  if (visibleSlides.length === 0) return 0;
  scheduler.schedule(() => {
    slides.forEach((slide) => {
      const isVisible = visibleSlides.includes(slide);
      slide.setAttribute("aria-hidden", "".concat(!isVisible));
    });
  });
  return visibleSlides.length;
};
let Slideshow = _Slideshow;
if (!customElements.get("slideshow-component")) {
  customElements.define("slideshow-component", Slideshow);
}
export {
  Slideshow
};
